/**
 * §31: план финальной сборки.
 *
 * Декларативное описание ролика целиком — что за чем идёт, где титры, где
 * логотип, какой формат на выходе. План строится до рендера и не зависит от
 * того, кто его исполнит: свой ffmpeg, внешний render-сервис или движок
 * из §82. Это и есть граница, за которой начинается открытый вопрос A1.
 *
 * Три следствия из того, что план — данные, а не код:
 *  1. рендерер подменяется без переписывания логики сборки;
 *  2. один и тот же план даёт один и тот же ролик;
 *  3. по отпечатку плана видно, изменилось ли что-нибудь с прошлой сборки.
 */
import { z } from 'zod';
import { captionLineSchema } from './captions';
import type { Notice } from './common';

/** Откуда берётся картинка клипа. */
export const clipSourceSchema = z
  .object({
    kind: z.enum(['video', 'image']),
    /** Ключ в хранилище. Ссылку рендерер получает сам — она живёт недолго. */
    storageKey: z.string().min(1),
    /** Длительность исходника, если известна: по ней решается подрезка. */
    sourceDurationSec: z.number().min(0).nullable().default(null),
  })
  .strict();

export const compositionClipSchema = z
  .object({
    sceneId: z.string().min(1),
    order: z.number().int(),
    /** Смещение от начала ролика. */
    startSec: z.number().min(0),
    durationSec: z.number().min(0.1),
    source: clipSourceSchema,
    /** Переход НА этот клип. У первого клипа перехода нет. */
    transition: z.enum(['none', 'cut', 'fade', 'dissolve']).default('cut'),
    /** Звук клипа: у сцены с речью он внутри видео, у графики его нет. */
    hasAudio: z.boolean().default(false),
  })
  .strict();

/** Титры одной сцены, сдвинутые в абсолютное время ролика. */
export const compositionCaptionTrackSchema = z
  .object({
    sceneId: z.string().min(1),
    startSec: z.number().min(0),
    lines: z.array(captionLineSchema),
    position: z.enum(['top', 'center', 'bottom']),
  })
  .strict();

/** Постоянные наложения: логотип на весь ролик (§13). */
export const compositionOverlaySchema = z
  .object({
    kind: z.literal('logo'),
    storageKey: z.string().min(1),
    position: z.string(),
    sizePercent: z.number(),
    opacity: z.number(),
    safeAreaPercent: z.number(),
  })
  .strict();

/**
 * §31, B3: фоновая музыка под весь ролик.
 *
 * Громкость относительная: музыка звучит тише речи. Абсолютные децибелы
 * зависят от того, насколько громкой вышла озвучка, — это задача рендерера.
 */
export const compositionMusicSchema = z
  .object({
    /** Ключ готовой дорожки, если она уже сгенерирована. */
    storageKey: z.string().min(1).nullable().default(null),
    /** Настроение словами — на случай, если дорожку ещё предстоит создать. */
    mood: z.string(),
    /** Доля от громкости речи, 0-1. */
    volume: z.number().min(0).max(1),
  })
  .strict();

/**
 * Закадровый голос (§19.3, §23).
 *
 * Сцена без человека — картинка, вставка, графика — молчит: своей дорожки
 * у неё нет. На готовом ролике это слышно как обрыв: речь, тишина, речь.
 * Закадровый голос — та же озвучка голосом человека, только звучит поверх
 * кадра, в котором его нет.
 *
 * Отдельно от `clips`, потому что это не клип: у него нет картинки, и
 * привязан он ко времени ролика, а не к дорожке видео.
 */
export const compositionVoiceoverSchema = z
  .object({
    sceneId: z.string().min(1),
    storageKey: z.string().min(1),
    startSec: z.number().min(0),
    durationSec: z.number().min(0.1),
  })
  .strict();

export const compositionPlanSchema = z
  .object({
    /** Версия формата плана: старые планы должны читаться после изменений. */
    version: z.literal(1).default(1),
    projectId: z.string().min(1),
    title: z.string(),

    format: z
      .object({
        aspectRatio: z.string(),
        width: z.number().int().positive(),
        height: z.number().int().positive(),
        fps: z.number().int().positive(),
      })
      .strict(),

    durationSec: z.number().min(0),
    clips: z.array(compositionClipSchema),
    captions: z.array(compositionCaptionTrackSchema).default([]),
    overlays: z.array(compositionOverlaySchema).default([]),

    /** Закадровый голос сцен без человека. */
    voiceovers: z.array(compositionVoiceoverSchema).default([]),

    /** Фоновая музыка (B3). `null` — ролик без музыки. */
    music: compositionMusicSchema.nullable().default(null),

    /** Оформление: цвета и шрифты нужны рендереру для титров (§27). */
    brandContext: z.unknown(),
  })
  .strict();

export type ClipSource = z.infer<typeof clipSourceSchema>;
export type CompositionClip = z.infer<typeof compositionClipSchema>;
export type CompositionCaptionTrack = z.infer<typeof compositionCaptionTrackSchema>;
export type CompositionOverlay = z.infer<typeof compositionOverlaySchema>;
export type CompositionMusic = z.infer<typeof compositionMusicSchema>;
export type CompositionPlan = z.infer<typeof compositionPlanSchema>;

/** Состояние сборки на экране проекта (§74). */
export interface ProjectRenderView {
  status: 'none' | 'queued' | 'rendering' | 'ready' | 'failed';
  statusLabel: string;
  /** 0-100, пока идёт сборка. */
  progress: number;
  /** Что мешает собрать ролик прямо сейчас. Пусто — значит можно. */
  blockers: Notice[];
  /** Стоимость сборки в единицах. */
  units: number;
  videoUrl: string | null;
  posterUrl: string | null;
  errorCode: string | null;
  /**
   * Собранный ролик соответствует текущему состоянию проекта.
   * Ложь означает: что-то поправили после сборки, есть смысл пересобрать.
   */
  upToDate: boolean;
}

export const renderStatusLabels: Record<ProjectRenderView['status'], string> = {
  none: 'Не собрано',
  queued: 'В очереди',
  rendering: 'Собираем',
  ready: 'Готово',
  failed: 'Не удалось',
};
